import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AiSearchChat.css';

function AiSearchChat() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pageRef = useRef(null);

  const quickPrompts = [
    'Show available assets',
    'Show assigned assets by department',
    'Show assigned laptops in IT department',
    'List employees with no active assets',
    'Show pending repairs'
  ];

  useEffect(() => {
    const mainContent = pageRef.current?.closest('.main-content');
    if (mainContent) {
      mainContent.classList.add('ai-chat-active');
    }
    return () => {
      if (mainContent) {
        mainContent.classList.remove('ai-chat-active');
      }
    };
  }, []);

  useEffect(() => {
    const savedMessages = localStorage.getItem('saralAssetGPT_messages');
    const savedHistory = localStorage.getItem('saralAssetGPT_history');

    if (savedMessages && savedHistory) {
      setMessages(JSON.parse(savedMessages));
      setConversationHistory(JSON.parse(savedHistory));
      return;
    }

    setMessages([
      {
        id: Date.now(),
        role: 'assistant',
        content: "I'm SaralAssetGPT. Ask anything about assets, assignments, employees, repairs, departments, or vendors.",
        timestamp: new Date()
      }
    ]);
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('saralAssetGPT_messages', JSON.stringify(messages));
      localStorage.setItem('saralAssetGPT_history', JSON.stringify(conversationHistory));
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, conversationHistory]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 140)}px`;
  }, [inputMessage]);

  const handleSendMessage = async (rawMessage) => {
    const userMessage = (rawMessage ?? inputMessage).trim();
    if (!userMessage || isTyping) return;

    setInputMessage('');
    setErrorMessage('');

    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    const nextHistory = [...conversationHistory, { Role: 'user', Content: userMessage }];
    setMessages(prev => [...prev, userMsg]);
    setConversationHistory(nextHistory);
    setIsTyping(true);

    try {
      const response = await axios.post('/api/aisearch/chat', {
        Message: userMessage,
        ConversationHistory: nextHistory
      });

      const aiMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data?.response || 'No response generated.',
        data: response.data?.showTable ? (response.data?.data || []) : null,
        suggestedOptions: response.data?.suggestedOptions || [],
        interpretation: response.data?.interpretation || '',
        confidence: response.data?.confidence,
        sources: response.data?.sources || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMsg]);
      setConversationHistory(prev => [...prev, { Role: 'assistant', Content: response.data.response }]);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Could not reach AI search service. Try again.');
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: 'Request failed. Please retry, or ask with a simpler query.',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const clearHistory = () => {
    localStorage.removeItem('saralAssetGPT_messages');
    localStorage.removeItem('saralAssetGPT_history');
    setMessages([
      {
        id: Date.now(),
        role: 'assistant',
        content: 'Chat history cleared. Ask your next question.',
        timestamp: new Date()
      }
    ]);
    setConversationHistory([]);
    setErrorMessage('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      field: key,
      label: key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()),
      render: (value) => {
        if (value === null || value === undefined || value === '') return 'N/A';
        if (typeof value === 'number' && (key.toLowerCase().includes('price') || key.toLowerCase().includes('value') || key.toLowerCase().includes('cost'))) {
          return `INR ${value.toLocaleString()}`;
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return value.toString();
      }
    }));
  };

  const getConfidenceClass = (confidence) => {
    if (typeof confidence !== 'number') return 'text-secondary';
    if (confidence >= 0.8) return 'text-success';
    if (confidence >= 0.6) return 'text-warning';
    return 'text-secondary';
  };

  return (
    <div className="saral-gpt-page" ref={pageRef}>
      <div className="saral-gpt-shell">
        <div className="saral-gpt-header">
          <div>
            <h3>SaralAssetGPT</h3>
            <p>Ask questions over your assets database</p>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={clearHistory}>
            <i className="fas fa-trash me-1"></i>Clear Chat
          </button>
        </div>

        <div className="saral-gpt-prompts">
          {quickPrompts.map((prompt) => (
            <button
              key={prompt}
              className="btn btn-sm"
              onClick={() => handleSendMessage(prompt)}
              disabled={isTyping}
            >
              {prompt}
            </button>
          ))}
        </div>

        <div className="saral-gpt-body">
          {messages.map((message) => (
            <div key={message.id} className={`saral-gpt-msg-row ${message.role === 'user' ? 'user' : 'assistant'}`}>
              <div className="saral-gpt-avatar">
                <i className={`fas fa-${message.role === 'assistant' ? 'robot' : 'user'}`}></i>
              </div>
              <div className="saral-gpt-msg">
                <div className="saral-gpt-meta">
                  <strong>{message.role === 'assistant' ? 'SaralAssetGPT' : 'You'}</strong>
                  <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>

                <div className="saral-gpt-bubble">
                  {message.role === 'assistant' ? (
                    <>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => <p {...props} />,
                          ul: ({ node, ...props }) => <ul {...props} />,
                          ol: ({ node, ...props }) => <ol {...props} />,
                          code: ({ node, inline, ...props }) => inline ? <code {...props} /> : <code className="block" {...props} />
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                      <button className="saral-gpt-copy" onClick={() => copyToClipboard(message.content)} title="Copy response">
                        <i className="fas fa-copy"></i>
                      </button>
                    </>
                  ) : (
                    <div>{message.content}</div>
                  )}
                </div>

                {message.interpretation && (
                  <div className="saral-gpt-interpretation">
                    Interpretation: {message.interpretation}
                    {typeof message.confidence === 'number' ? (
                      <>
                        {' | '}
                        <span className={getConfidenceClass(message.confidence)}>
                          Confidence: {Math.round(message.confidence * 100)}%
                        </span>
                      </>
                    ) : ''}
                  </div>
                )}

                {!!message.sources?.length && (
                  <div className="saral-gpt-sources">
                    <div className="saral-gpt-sources-title">
                      <i className="fas fa-link me-1"></i>Evidence
                    </div>
                    <div className="saral-gpt-sources-list">
                      {message.sources.map((source) => (
                        <span key={`${source.id}-${source.title}`} className="saral-gpt-source-chip">
                          <strong>{source.id}</strong>: {source.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {!!message.suggestedOptions?.length && (
                  <div className="saral-gpt-actions">
                    {message.suggestedOptions.map((option) => (
                      <button key={option} className="btn btn-sm" onClick={() => setInputMessage(option)}>
                        {option}
                      </button>
                    ))}
                  </div>
                )}

                {!!message.data?.length && (
                  <div className="saral-gpt-table-wrap">
                    <div className="saral-gpt-table-title">{message.data.length} records</div>
                    <div className="saral-gpt-table">
                      <DataTable data={message.data} columns={generateColumns(message.data)} itemsPerPage={10} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="saral-gpt-msg-row assistant">
              <div className="saral-gpt-avatar"><i className="fas fa-robot"></i></div>
              <div className="saral-gpt-msg">
                <div className="saral-gpt-meta"><strong>SaralAssetGPT</strong></div>
                <div className="saral-gpt-bubble typing">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="saral-gpt-composer-wrap">
          {errorMessage && <div className="alert alert-danger py-2 mb-2">{errorMessage}</div>}
          <div className="saral-gpt-composer">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
              placeholder="Ask about assets, employees, assignments, repairs..."
              disabled={isTyping}
              rows={1}
            />
            <button onClick={() => handleSendMessage()} disabled={!inputMessage.trim() || isTyping}>
              {isTyping ? <div className="spinner-border spinner-border-sm text-light" role="status"></div> : <i className="fas fa-paper-plane"></i>}
            </button>
          </div>
          <div className="saral-gpt-help">Enter to send, Shift+Enter for newline</div>
        </div>
      </div>
    </div>
  );
}

export default AiSearchChat;
